// AI Functions client (EXT supplier portal).
//
// Browser-direct upload of supplier-submitted documents (invoices,
// supporting docs) into bldt-ai-fn. Mirrors the V1 browser-direct
// upload pattern — bldt-api-fn is intentionally bypassed for multipart
// to avoid a 3-hop file body.
//
// Reads (get_documents) go through the proxy chain via useFetch; this
// module is upload-only.

const AI_FN_URL = process.env.REACT_APP_AI_FN_URL || 'http://localhost:7075/api';

// Environment scope used to segregate AI document uploads across
// local/dev/qa/prod. Derived from the configured API host instead of a
// separate REACT_APP_ENV var so the two cannot drift out of sync — the
// hostname already encodes which environment this build targets.
const AI_ENVIRONMENT = (() => {
	const url = process.env.REACT_APP_API_URL || '';
	if (/localhost|127\.0\.0\.1/.test(url)) return 'local';
	if (/apidev\./.test(url)) return 'dev';
	if (/apiqa\./.test(url)) return 'qa';
	if (/\.worldbank\.org/.test(url)) return 'prod';
	return 'unknown';
})();

export interface SupplierUploadPayload {
	file: File;
	docType: string;
	contractId: number;
	projectId: number;
	supplierEntityId?: number;
	/**
	 * Optional requirement-field tagging. When the upload originates from a
	 * specific section/field on a condition (milestone), pass these so the
	 * blob's metadata carries enough lineage for bldt-ui to surface the file
	 * back against the same field at prepare-payment time. Without these
	 * tags, the blob is just contract-scoped and a maker has to re-attach
	 * it manually.
	 */
	conditionId?: number;
	sectionId?: number | string;
	fieldId?: number | string;
	date?: string;
}

export interface SupplierUploadResponse {
	document_id?: number | string;
	blob_uri?: string;
	job_id?: string;
	error?: string;
}

export const uploadSupplierDocument = async (
	payload: SupplierUploadPayload
): Promise<SupplierUploadResponse> => {
	const token = sessionStorage.getItem('token');

	const formData = new FormData();
	formData.append('file', payload.file);
	formData.append('doc_type', payload.docType);
	formData.append('contract_id', String(payload.contractId));
	formData.append('project_id', String(payload.projectId));
	formData.append('source_type', 'contract');
	formData.append('source_id', String(payload.contractId));

	// Tag convention (story 50): comma-separated {entityType}:{id} pairs.
	const tags = [`contract:${payload.contractId}`];
	if (payload.supplierEntityId != null) {
		tags.push(`supplier:${payload.supplierEntityId}`);
	}
	if (payload.conditionId != null) {
		tags.push(`condition:${payload.conditionId}`);
	}
	if (payload.sectionId != null) {
		tags.push(`section:${payload.sectionId}`);
	}
	if (payload.fieldId != null) {
		tags.push(`requirement_field:${payload.fieldId}`);
	}
	formData.append('tag', tags.join(','));

	formData.append('environment', AI_ENVIRONMENT);
	if (payload.date) {
		formData.append('date', payload.date);
	}

	const res = await fetch(`${AI_FN_URL}/upload_document`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
		body: formData,
	});

	if (!res.ok) {
		const errBody = await res.json().catch(() => ({}));
		throw new Error(errBody.error || `Upload failed: ${res.status}`);
	}

	return res.json();
};

// ---------------------------------------------------------------------------
// Blob reads (view / download) via the bldt-ai-fn read_blob endpoint.
// Uploaded documents live in the `bronze` container keyed by filename.
// ---------------------------------------------------------------------------

interface AiBlobResponse {
	content?: unknown;
	content_base64?: string;
	content_type?: string;
}

const inferContentType = (filename: string, apiContentType?: string): string => {
	if (apiContentType && apiContentType !== 'application/octet-stream') return apiContentType;
	const ext = filename.split('.').pop()?.toLowerCase();
	const map: Record<string, string> = {
		pdf: 'application/pdf',
		doc: 'application/msword',
		docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		xls: 'application/vnd.ms-excel',
		xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		csv: 'text/csv',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		gif: 'image/gif',
		webp: 'image/webp',
		txt: 'text/plain',
		json: 'application/json',
		html: 'text/html',
		xml: 'application/xml',
	};
	return (ext && map[ext]) || apiContentType || 'application/octet-stream';
};

export const fetchAiBlob = async (blob: string, container = 'bronze'): Promise<AiBlobResponse> => {
	const token = sessionStorage.getItem('token');
	const qs = `?container=${encodeURIComponent(container)}&blob=${encodeURIComponent(blob)}`;
	const res = await fetch(`${AI_FN_URL}/read_blob${qs}`, {
		method: 'GET',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
	});
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `Failed to read document: ${res.status}`);
	}
	return res.json();
};

const blobFromReadResponse = (data: AiBlobResponse, contentType: string): Blob => {
	if (data.content_base64) {
		const byteString = atob(data.content_base64);
		const bytes = new Uint8Array(byteString.length);
		for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
		return new Blob([bytes], { type: contentType });
	}
	if (typeof data.content === 'string') return new Blob([data.content], { type: contentType });
	return new Blob([JSON.stringify(data.content, null, 2)], { type: 'application/json' });
};

// Build an object URL for embedding/viewing a document in the browser.
export const fetchAiBlobObjectUrl = async (blob: string, container = 'bronze'): Promise<string> => {
	const data = await fetchAiBlob(blob, container);
	return URL.createObjectURL(blobFromReadResponse(data, inferContentType(blob, data.content_type)));
};

// Download a document to the user's machine.
export const downloadAiDocument = async (filename: string, container = 'bronze'): Promise<void> => {
	const objectUrl = await fetchAiBlobObjectUrl(filename, container);
	const a = document.createElement('a');
	a.href = objectUrl;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
};
